import {ReviewerSchema, type Reviewer} from '@github-ui/dotcom-schema/types/pull_requests/v1/reviewer'
import {reviewState, reviewStateTooltip} from '../review-state-helpers'
import type {TeamCompositeState} from '../team-review-state-helpers'
import {create} from '@github-ui/dotcom-schema/protobuf'
import type {
  CompletedReview,
  PendingReviewRequest,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import styles from '../Reviewers.module.css'
import {reviewerDisplayName, ReviewerIdentity} from '../ReviewerIdentity'
import {IconButton} from '@primer/react'
import {ShieldIcon, SyncIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useRef, type ReactNode} from 'react'
import {useReRequestReviewMutation} from '../../../../mutations/use-re-request-review-mutation'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {ReviewStatusIndicator} from '../ReviewStatusIcons'
import {announce} from '@github-ui/aria-live'

export function TeamCompositeRow({
  team,
  onMutationStart,
  onMutationError,
  onMutationSuccess,
}: {
  team: TeamCompositeState
  onMutationStart?: () => void
  onMutationError?: (reviewerName: string | undefined) => (origin: Element | null) => void
  onMutationSuccess?: (reviewerName: string | undefined) => () => void
}) {
  const {icon: stateIcon, className: stateClass} = reviewState(team.compositeState)
  const syntheticReviewer: Reviewer =
    team.teamReviewer ?? create(ReviewerSchema, {displayLogin: team.teamName, reviewerType: 'Team'})
  const hasAvatar = syntheticReviewer.avatarUrl !== ''
  const nestedItems: NestedTeamMemberItem[] = [
    ...team.completedUserReviews.map(review => ({kind: 'completed', review}) as const),
    ...team.pendingUserRequests.map(request => ({kind: 'pending', request}) as const),
  ].filter(item => nestedTeamMemberReviewer(item) !== null)
  nestedItems.sort((a, b) => nestedTeamMemberSortName(a).localeCompare(nestedTeamMemberSortName(b)))

  return (
    <li>
      <div className={styles.reviewerRow}>
        {hasAvatar ? (
          <ReviewerIdentity reviewer={syntheticReviewer} />
        ) : (
          <span className={styles.reviewerIdentity}>
            <span className={styles.reviewerName}>{team.teamName}</span>
          </span>
        )}
        <span className={styles.indicators}>
          {team.isCodeOwner && (
            <IconButton
              icon={ShieldIcon}
              aria-label={`${team.teamName} is a code owner`}
              variant="invisible"
              size="small"
              tooltipDirection="nw"
              className={clsx(styles.statusIcon, 'color-fg-muted')}
            />
          )}
          {nestedItems.length === 0 && (
            <IconButton
              icon={stateIcon}
              aria-label={`${team.teamName}: ${team.stateLabel}`}
              variant="invisible"
              size="small"
              tooltipDirection="nw"
              className={clsx(styles.statusIcon, stateClass)}
            />
          )}
        </span>
      </div>
      {nestedItems.length > 0 && (
        <ul className={styles.nestedMemberList} aria-label={`Members of ${team.teamName}`}>
          {nestedItems.map(item => (
            <NestedTeamMemberRow
              key={nestedTeamMemberKey(item)}
              item={item}
              onMutationStart={onMutationStart}
              onMutationError={onMutationError}
              onMutationSuccess={onMutationSuccess}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

type NestedTeamMemberItem =
  | {kind: 'completed'; review: CompletedReview}
  | {kind: 'pending'; request: PendingReviewRequest}

function nestedTeamMemberReviewer(item: NestedTeamMemberItem): Reviewer | null {
  const reviewer = item.kind === 'completed' ? item.review.reviewer : item.request.reviewer
  return reviewer ?? null
}

function nestedTeamMemberSortName(item: NestedTeamMemberItem): string {
  const reviewer = nestedTeamMemberReviewer(item)
  return reviewer ? reviewerDisplayName(reviewer) : ''
}

function nestedTeamMemberKey(item: NestedTeamMemberItem): string {
  const reviewer = nestedTeamMemberReviewer(item)
  const id = reviewer?.id.toString() ?? '0'
  return item.kind === 'completed' ? `nested-completed-${item.review.id}` : `nested-pending-${id}`
}

function NestedTeamMemberRow({
  item,
  onMutationStart,
  onMutationError,
  onMutationSuccess,
}: {
  item: NestedTeamMemberItem
  onMutationStart?: () => void
  onMutationError?: (reviewerName: string | undefined) => (origin: Element | null) => void
  onMutationSuccess?: (reviewerName: string | undefined) => () => void
}) {
  const mutationOriginRef = useRef<Element | null>(null)
  const reviewer = nestedTeamMemberReviewer(item)
  const displayName = reviewer ? reviewerDisplayName(reviewer) : undefined
  const {mutate: reRequestReview, isPending: isReRequesting} = useReRequestReviewMutation({
    onError: () => onMutationError?.(displayName)(mutationOriginRef.current),
    onSuccess: onMutationSuccess?.(displayName),
  })
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  if (!reviewer || !displayName) return null

  const canReRequestReview = item.kind === 'completed' && item.review.canReRequest

  let statusIndicator: ReactNode
  if (item.kind === 'completed') {
    const {icon, className} = reviewState(item.review.state)
    statusIndicator = (
      <IconButton
        icon={icon}
        aria-label={reviewStateTooltip(item.review.state, displayName)}
        variant="invisible"
        size="small"
        tooltipDirection="nw"
        className={clsx(styles.statusIcon, className)}
      />
    )
  } else {
    statusIndicator = <ReviewStatusIndicator label={`Awaiting requested review from ${displayName}`} />
  }

  const requestReview = () => {
    if (isReRequesting) return
    mutationOriginRef.current = document.activeElement
    onMutationStart?.()
    announce(`Re-requesting review from ${displayName}`)
    reRequestReview({reviewerId: Number(reviewer.id)})
  }

  return (
    <li className={clsx(styles.reviewerRow, styles.nestedReviewerItem)}>
      <span className={clsx(styles.connector, styles.connectorLast)} aria-hidden="true" />
      <ReviewerIdentity reviewer={reviewer} />
      <span className={styles.indicators}>
        {canReRequestReview && (
          <IconButton
            icon={SyncIcon}
            aria-label={`Re-request review from ${displayName}`}
            variant="invisible"
            size="small"
            inactive={isReRequesting || undefined}
            aria-disabled={isReRequesting || undefined}
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
            onClick={() => {
              if (isReRequesting) return
              sendPullRequestAnalyticsEvent('overview.re_request_review', 'OVERVIEW_REVIEWER')
              requestReview()
            }}
          />
        )}
        {statusIndicator}
      </span>
    </li>
  )
}
